<template>
    <f7-page
        class="new-payment-view"
        ref="self"
        @page:beforein="pageBeforeIn"
        @page:afterout="pageAfterOut"
        @page:afterin="pageAfterIn"
    >
        <div class="title-wrapper">
            <p class="title">{{ $t("newPayment.title") }}</p>
        </div>
        <div v-if="vc.isFractionalNumbersView.value">
            <div class="is-fractional-number-view">
                <div class="vpcape_left-side">
                    <i class="icon svg-warning"></i>
                </div>
                <div class="vpcape_right-side">
                    {{ $t("payment.fractional-numbers") }}
                </div>
            </div>
        </div>
        <ViewPaymentConfigAgentButton
            v-if="isCanSelectAgent && currency"
            @toSelectAgent="toSelectAgent"
            :agent="agent"
            :currency="currency"
        />
        <f7-list class="list-strong list-outline">
            <f7-block-header class="display-flex justify-content-space-between">
                <span class="title">{{ $t("newPayment.sum") }}</span>
                <div class="minmax-wrapper">
                    <Transition name="fade" mode="out-in">
                        <template v-if="isAgentsRangesLoading">
                            <div class="min-max-loader center"><span></span></div>
                        </template>
                        <template v-else>
                            <span class="minmax" :class="[{'minmax-error': textMaxMinError}]">{{ textMaxMin }}</span>
                        </template>
                    </Transition>
                </div>
            </f7-block-header>
            <f7-list-input
                ref="amountInput"
                type="text"
                inputmode='decimal'
                class="input-amount"
                placeholder=""
                :class="{'focused': inputFocused}"
                v-model:value="amount"
                @focus="amountInputFocus"
                @blur="amountInputBlur"
                v-format-number
            >
                <template #content>
                    <Transition name="fade" mode="out-in">
                        <div class="content" v-if="textMaxMin.length > 0" @click="clickMax">
                            <span class="max-amount">MAX</span>
                        </div>
                        <div v-else class="content"></div>
                    </Transition>
                </template>
            </f7-list-input>
            <div class="default-amounts block" ref="default-amounts">
                <div class="default-amount" v-for="amount in suggestedAmounts"
                     :key="amount"
                     @pointerleave="defaultAmountClick(amount)">
                    <span class="amount" v-format-number>{{ amount }} {{ currency.abbr }}</span>
                </div>
            </div>
        </f7-list>
        <f7-block class="actions">
            <f7-button
                class="theme-type"
                preloader
                :disabled="submitLoading"
                :loading="submitLoading"
                @click="submit"
            >{{ $t("newPayment.next") }}
            </f7-button>
        </f7-block>
    </f7-page>
</template>

<script lang="ts" setup>
import NewPaymentController from "./NewPaymentController";
import {computed, onMounted, Ref, ref, ToRef, watch} from "vue";
import {f7} from "framework7-vue";
import Animations from "@/helpers/animations";
import ErrorsService from "@/services/errors-service/ErrorsService";
import ViewPaymentConfigAgentButton from "./comps/ViewPaymentCreateConfigAgentButton.vue";
import RouterService from "@/services/RouterService";
// @ts-ignore
import AppController from "@target/components/App/ts/AppController";
import LightPaymentPidResponseError from "@models/responses/light-payment-pid/LightPaymentPidResponseError";
import ViewPaymentCreateAgentsController from "@/views/payment-create-agents/ViewPaymentCreateAgentsController";
import AgentsAmountRanges from "@models/errors/components/AgentsAmountRanges";
import {FirebaseService} from "@/services/firebase/FirebaseService";
import IFrameService from "@/services/iframe/IFrameService";
import {FirebaseEvents} from "@/services/firebase/analytics/FirebaseEvents";
import FirebaseEventBuilder from "@/services/firebase/analytics/FirebaseEventBuilder";
import PaymentService from "@/services/operations/payment/PaymentService";
import {AccountError} from "@enums/ServerErrorCodes";
import ServiceAccount from "@/services/v2/data/service-account/ServiceAccount";
import {getDevice} from "framework7/shared/get-device";

console.log('payment')
const paymentError: ToRef<LightPaymentPidResponseError> = AppController.getInstance().paymentService.paymentError;
const routerService: RouterService = AppController.getInstance().routerService;
const paymentService: PaymentService = AppController.getInstance().paymentService;
const account = ServiceAccount.of().account;
const vc = NewPaymentController.getInstance();

const inputFocused = ref(false);
const {
    selectedPaymentSystem,
    amount,
    currency,
    isAgentsRangesLoading,
    agentsAmountRanges,
    agent,
    isCanSelectAgent
} = vc;
const submitLoading = ref(false);
const self = ref(null);
const textMaxMinError = ref(false);
const suggestedAmounts = computed(() => {
    return account.value?.currency.dSums ?? [1500, 3000, 5000, 7000];
});
const amountInput = ref(null);
const textMaxMin = computed(() => {
    if (!agentsAmountRanges.value) return "";

    const {min, max} = agentsAmountRanges.value;
    if (min == null || max == null) return "";
    if (min == 0 || max == 0) return "";

    return formatText(
            Math.ceil(min).toString()
        ) + ` ${currency.value.abbr} - `
        +
        formatText(
            Math.floor(max).toString()
        ) + ` ${currency.value.abbr}`
});

onMounted(async () => {
    await NewPaymentController.getInstance().init();
    await fetchAgentsAmountRangesUseCase();
    // setAmountValueLikeFirstDSum();
});

function setAmountValueLikeFirstDSum() {
    if (account.value?.currency.dSums) {
        vc.amount.value = account.value.currency.dSums[0].toString() + ` ${currency.value.abbr}`;
    }
}
async function fetchAgentsAmountRangesUseCase() {
    const minMaxValues = await NewPaymentController.getInstance().fetchAgentsAmountRanges();
    if (minMaxValues && minMaxValues.min) {
        checkAndUpdateAmountInputWithFloorAgentsAmount(amount, minMaxValues.min, currency);
    }
    if (minMaxValues && minMaxValues.max) {
        checkAndUpdateAmountInputWithTopAgentsAmount(amount, minMaxValues.max, currency);
    }
}

function checkAndUpdateAmountInputWithFloorAgentsAmount(amount: ToRef<string>, minAgentAmount: number, currency: Ref<any>) {
    const amountValue = getFloatAmountFromInput(amount);
    if (amountValue < minAgentAmount) {
        amount.value = Math.ceil(minAgentAmount).toString() + ` ${currency.value.abbr}`;
    }
}

function checkAndUpdateAmountInputWithTopAgentsAmount(amount: ToRef<string>, maxAgentAmount: number, currency: Ref<any>) {
    const amountValue = getFloatAmountFromInput(amount);
    if (amountValue > maxAgentAmount) {
        amount.value = Math.floor(maxAgentAmount).toString() + ` ${currency.value.abbr}`;
    }
}

function getFloatAmountFromInput(amount: ToRef<string>) {
    const amountValue = parseFloat(amount.value.replace(/\s/g, ''));
    if (isNaN(amountValue)) return 0;
    return amountValue;
}

watch(agent, (newAgent) => {
    if (newAgent !== null) {
        vc.agentsAmountRanges.value = new AgentsAmountRanges(newAgent.sumRanges.currency.min, newAgent.sumRanges.currency.max);
        if (vc.agentsAmountRanges.value && vc.agentsAmountRanges.value.min) {
            checkAndUpdateAmountInputWithFloorAgentsAmount(
                amount,
                vc.agentsAmountRanges.value.min,
                currency
            );
        }
        if (vc.agentsAmountRanges.value && vc.agentsAmountRanges.value.max) {
            checkAndUpdateAmountInputWithTopAgentsAmount(
                amount,
                vc.agentsAmountRanges.value.max,
                currency
            );
        }
    } else {
        vc.fetchAgentsAmountRanges();
    }
});
watch(selectedPaymentSystem, () => {
    textMaxMinError.value = false;
});

watch(amount, (newValue: string, oldValue: string) => {
    let re = /[^0-9.,$₽]/gi;
    amount.value = newValue.replace(re, '').replace(',', '').replace('.', '');
});

function pageAfterIn() {
    // console.log(vc.afterInFunction)
    // if (vc.afterInFunction) {
    //     vc.afterInFunction();
    // }
}

function pageAfterOut() {
    // console.log('pageAfterOut')
}

async function toSelectAgent() {
    textMaxMinError.value = false;
    await routerService.toViewPaymentCreateAgents(agent.value, null, paymentError.value, vc.setAgent.bind(vc));
}

function closeKeyboardFocusInput(callback: Function) {
    // @ts-ignore
    const inputEl: HTMLInputElement = (amountInput.value.$el as HTMLElement)!;
    if (getDevice().cordova && inputEl.classList.contains("focused")) {
        inputEl.blur();
        setTimeout(async () => {
            await callback();
        }, 500);
        return false;
    }
    callback();
}

const amountInputKeydown = (e: any) => {
    if ((e.keyCode < 48 || e.keyCode > 57) && e.keyCode !== 8) {
        e.preventDefault();
        return;
    }
}
const pageBeforeIn = () => {
    setTimeout(() => {
        detectHeight();
    }, 0)
}
const clickMax = () => {
    if (!agentsAmountRanges.value) return false;

    const {min, max} = agentsAmountRanges.value;
    if (min == null || max == null) return false;
    if (min == 0 || max == 0) return false;

    amount.value = Math.floor(max).toString() + ` ${currency.value.abbr}`;
}
const defaultAmountClick = (value: number) => {
    closeKeyboardFocusInput(() => {
        amount.value = `${value} ${currency.value.abbr}`;
    });
}
const detectHeight = () => {
    if (IFrameService.of().isIFrameView) return;

    const mainEl = (self.value! as any);
    let height = 0;
    const style = getComputedStyle(document.body);
    const children = mainEl.$el.querySelector('.page-content').children;

    for (let i = 0; i < children.length; i++) {
        const el = (children[i] as HTMLElement);
        const styleEl = getComputedStyle(el);
        // console.log(el.offsetHeight + " " +  parseFloat(styleEl['marginTop']) + " " + parseFloat(styleEl['marginBottom']))
        height += el.clientHeight + parseFloat(styleEl['marginTop']) + parseFloat(styleEl['marginBottom']);
    }

    if (vc.isFractionalNumbersView.value) {
        height += 20;
    }

    mainEl.$el.closest('.new-payment-popup').style.height = height + parseInt(style.getPropertyValue('--f7-safe-area-bottom')) + 'px';
}
const closePopup = () => {
    const popupEl: HTMLDivElement = document.querySelector('.new-payment-popup')!;
    f7.popup.get(popupEl).close();
}
const formatText = (text: string) => {
    let regex = new RegExp(currency.value.abbr, 'g');
    const sign = text.match(regex);
    text = text.replace(/\s/g, '');
    const parts = text.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    let result = parts.join(".");
    if (sign) {
        let regex = new RegExp(sign[0], 'g');
        result = result.replace(regex, ` ${sign[0]}`);
    }
    return result;
}
const submit = async () => {
    await closeKeyboardFocusInput(async () => {
        let isClosePopup = true;
        try {
            submitLoading.value = true;
            if (!vc.validateMinmax()) {
                isClosePopup = false;
                highlightMinmaxError();
                return;
            }
            FirebaseService.of().analyticsEvent(
                FirebaseEvents.PAYMENT_CREATING_TRY,
                FirebaseEventBuilder.of().build()
            );
            paymentService.payment.value = await vc.next();
            FirebaseService.of().analyticsEvent(
                FirebaseEvents.PAYMENT_CREATING_SUCCESS,
                FirebaseEventBuilder.of().build()
            );
            // TODO: refactor
            if (import.meta.env.VITE_APP_TYPE === 'light') {
                AppController.getInstance().paymentService.paymentError.value = null;
                isClosePopup = true;
            } else {
                f7.views.main.router.navigate('/payment');
            }
        } catch (e: any) {
            FirebaseService.of().analyticsEvent(
                FirebaseEvents.PAYMENT_CREATING_ERROR,
                FirebaseEventBuilder.of().build()
            );
            let callback = null;
            if (e.code === AccountError.AgentNotFound) {
                callback = () => {
                    if (import.meta.env.VITE_APP_TYPE === 'light') {
                        isClosePopup = true;
                        AppController.getInstance().paymentService.paymentError.value = e;
                    } else {
                        showNoAgentPopup();
                    }
                }
            }
            if (e.code === AccountError.AgentsUnavailableForAmount) {
                callback = async () => {
                    const responseError = e.data;
                    // @ts-ignore
                    if (responseError.data.ranges!.max < responseError.data.ranges!.min) {
                        if (import.meta.env.VITE_APP_TYPE === 'light') {
                            isClosePopup = true;
                            AppController.getInstance().paymentService.paymentError.value = e;
                        } else {
                            showNoAgentPopup();
                        }
                    } else {
                        isClosePopup = false;
                        vc.agent.value = null;

                        let iAmount = parseFloat(amount.value.replace(/\s/g, ''));
                        ViewPaymentCreateAgentsController.of().paymentErrorWasShowed.value = false;
                        await routerService.toViewPaymentCreateAgents(null, iAmount, new LightPaymentPidResponseError(e.code, null), vc.setAgent.bind(vc));
                    }
                }
            }
            if (e.code === AccountError.AgentsUnavailableForRangeAmount) {
                callback = async () => {
                    isClosePopup = false;
                    let iAmount = parseFloat(amount.value.replace(/\s/g, ''));
                    ViewPaymentCreateAgentsController.of().paymentErrorWasShowed.value = false;
                    await routerService.toViewPaymentCreateAgents(agent.value, iAmount, new LightPaymentPidResponseError(e.code, null), vc.setAgent.bind(vc));
                }
            }
            ErrorsService.of().handle(e, callback);
        } finally {
            submitLoading.value = false;
            if (isClosePopup) closePopup();
        }
    });
}
const showNoAgentPopup = () => {
    const el: HTMLDivElement = document.querySelector('.no-agents-sheet')!;
    const sheet = f7.sheet.create({
        el,
    });
    sheet.open();
}
const amountInputBlur = (e: Event) => {
  const targetElement = e.target as HTMLInputElement;
  const isEmpty = targetElement.value.length === 0;
  const timeout = getDevice().cordova ? 314 : 0;

  setTimeout(() => {
    inputFocused.value = false;
  }, timeout);

  const abbr = currency.value.abbr;
  const cleanedValue = targetElement.value.replace(/\D+/g, '');

  amount.value = isEmpty
      ? `${vc.calculateStartAmountValue()} ${abbr}`
      : `${Math.floor(Number(cleanedValue))} ${abbr}`;
}

function checkNeedSelectAgent() {
    return paymentError.value && paymentError.value.code === 10034 && agent.value === null;

}

const amountInputFocus = () => {
    textMaxMinError.value = false;
    inputFocused.value = true;
    amount.value = '';
}
const highlightMinmaxError = () => {
    textMaxMinError.value = true;
    const minmaxEl: HTMLElement = ((self.value! as any).$el as HTMLElement).querySelector('.minmax-wrapper')!;
    Animations.shakeAnimation(minmaxEl);
}
</script>

<style lang="scss">
.device-android, .device-ios {
    &.olimp, &.olimp-casino, &.olimpcasinokg {
        .new-payment-popup {
            //height: calc(var(--f7-safe-area-bottom) + 406px + 100px) !important;
            //height: calc(var(--f7-safe-area-bottom) + 406px) !important;
        }
    }
}

.new-payment-view {
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;

    .is-fractional-number-view {
        background: rgba(230, 151, 0, 0.1);
        border-radius: 12px;
        display: flex;
        color: rgba(207, 117, 41, 1);
        gap: 12px;
        padding: 12px;
        margin: 16px;
        //height: 44px;

        .vpcape {
            &_left-side {
                display: flex;
                align-items: center;
            }

            p {
                @include text(14px, 400, #CF7529, 16.8px);
            }
        }
    }

    .min-max-loader {
        position: relative;
        display: block;
        height: 14px;
        width: 14px;
    }

    .min-max-loader span {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        margin: auto;
        height: 14px;
        width: 14px;
        clip: rect(4px, 14px, 14px, 0);
        -webkit-animation: min-max-loader-3 0.7s linear infinite;
        animation: min-max-loader-3 0.7s linear infinite;
    }

    @-webkit-keyframes min-max-loader-3 {
        0% {
            -webkit-transform: rotate(0deg);
        }
        100% {
            -webkit-transform: rotate(360deg);
        }
    }
    @keyframes min-max-loader-3 {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    .min-max-loader span::after {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        margin: auto;
        height: 10px;
        width: 10px;
        border: 1px solid var(--grey2);
        border-radius: 50%;
    }

    .title-wrapper {
        margin: 24px 16px 0;

        .title {
            font-weight: 700;
            font-size: 24px;
            line-height: 26px;
            letter-spacing: 0.337647px;
            color: #000000;
        }
    }

    .bill-account_wrapper {
        .title {
            margin: 0 16px;
            font-style: normal;
            font-weight: 400;
            font-size: 12px;
            line-height: 18px;
            text-transform: uppercase;
            color: #818E95;
        }

        .title + .bill-account {
            margin-top: 8px;
        }

        .bill-account {
            cursor: pointer;
            position: relative;
            height: 48px;
            display: flex;
            background-color: #FFFFFF;
            //width: calc(100% - 32px);
            align-items: center;
            padding: 0px 16px;

            &:after {
                content: '';
                position: absolute;
                background-color: var(--f7-list-border-color);
                display: block;
                z-index: 15;
                top: auto;
                right: auto;
                bottom: 0;
                left: 0;
                height: 1px;
                width: 100%;
                transform-origin: 50% 100%;
                transform: scaleY(calc(1 / var(--f7-device-pixel-ratio)));
            }

            &:before {
                content: '';
                position: absolute;
                background-color: var(--f7-list-border-color);
                display: block;
                z-index: 15;
                top: 0;
                right: auto;
                bottom: auto;
                left: 0;
                height: 1px;
                width: 100%;
                transform-origin: 50% 0%;
                transform: scaleY(calc(1 / var(--f7-device-pixel-ratio)));
            }

            .left-side {
                display: flex;
                align-items: center;
                flex: 1;
                gap: 8px;

                .icon-bill {
                    width: 24px;
                    height: 24px;
                    background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_2594_15344)'%3E%3Crect width='24' height='24' fill='%23ACB4BE'/%3E%3Cpath d='M15.623 6.54584C15.7266 6.54551 15.8301 6.55117 15.9329 6.5628C15.9725 6.64171 16.0183 6.71735 16.0698 6.78901C16.2963 7.11704 16.6414 7.34417 17.0323 7.42239C16.4057 7.4665 15.9307 7.25048 15.623 6.54584ZM15.8492 6.38975C15.7779 6.21057 15.7271 6.02389 15.6977 5.83328C15.2543 5.18859 14.2092 5.13317 13.0273 5.49397C13.7591 6.51982 14.751 6.74151 15.8549 6.38975H15.8492ZM14.0837 5.07662C14.6492 5.03477 15.1265 5.12412 15.4749 5.34354C15.5404 5.38602 15.6024 5.43368 15.6604 5.48606C15.6507 5.30016 15.6545 5.11381 15.6717 4.92845C15.2475 4.3946 14.4671 4.11411 13.3496 4.11524C13.5203 4.48754 13.7732 4.81625 14.0893 5.07662H14.0837ZM17.0244 7.21089C17.255 7.46346 17.5029 7.69967 17.7664 7.91779C17.7031 7.77784 17.649 7.63389 17.6046 7.48686C17.4704 7.37842 17.342 7.26289 17.2201 7.14076C17.6057 6.23593 17.0934 5.37521 15.9386 4.50884C15.7079 5.73036 15.9963 6.95528 17.03 7.21089H17.0244ZM18.5977 9.92538C18.6293 9.96129 18.6588 9.99906 18.6859 10.0385C18.6885 9.87794 18.707 9.71805 18.7413 9.56119C18.7392 9.3724 18.7008 9.18577 18.6282 9.0115C18.3517 8.78421 18.1127 8.51473 17.9202 8.21299C17.527 7.97445 17.0922 7.8125 16.6387 7.73569C16.779 9.0624 17.6499 9.9152 18.6033 9.92538H18.5977ZM18.851 8.92667C18.848 9.0022 18.851 9.07784 18.8601 9.15288C18.9389 8.9423 19.0354 8.73874 19.1485 8.54438C19.3668 7.68253 19.0275 6.6646 17.5243 6.10247C17.7777 7.62485 17.9496 8.18358 18.8567 8.92667H18.851ZM19.0094 10.7454C19.1685 10.9107 19.3102 11.0918 19.4324 11.286C20.2445 10.7013 20.2897 9.46052 19.6586 8.14173C18.8657 9.23771 18.7594 10.0249 19.015 10.7454H19.0094ZM17.8998 10.0792C18.0966 12.1411 18.2561 12.6365 19.2016 13.3898C19.1388 12.8336 19.2234 12.2707 19.4471 11.7577C19.2333 11.2057 18.7515 10.6787 17.9055 10.0792H17.8998ZM20.3643 10.7284C19.2333 12.0212 19.2616 13.1997 19.6043 14.2064C19.5008 14.2931 19.3868 14.3665 19.265 14.4247L19.1982 14.4597C19.2588 14.3381 19.3113 14.2127 19.3555 14.0842C19.3007 13.8989 19.2591 13.7099 19.231 13.5187C19.1609 14.1589 18.7786 14.7922 18.4619 15.1689C18.6489 15.0407 18.8456 14.9273 19.0501 14.8296C19.1631 14.7383 19.2813 14.6537 19.4041 14.5762C19.5343 14.4954 19.6583 14.4051 19.7751 14.3059C20.5261 14.0921 21.1323 12.4385 20.3643 10.7284ZM17.8331 15.6892C17.9247 15.5964 18.022 15.5059 18.126 15.4188C18.1923 15.2688 18.2453 15.1133 18.2844 14.954C18.7447 14.2437 18.5422 13.3196 17.7811 12.0947C17.2155 13.3694 17.4644 14.5739 18.0186 15.049C17.9639 15.1897 17.8981 15.3259 17.8218 15.4562C17.8274 15.5353 17.832 15.6134 17.832 15.6892H17.8331ZM17.2257 17.2047C17.1546 17.2158 17.0848 17.2344 17.0176 17.2602C17.0368 16.8179 17.2743 16.318 17.6713 15.8622C17.6884 15.3587 17.599 14.8573 17.4089 14.3907C16.486 15.2605 16.391 16.1778 16.788 17.2466C16.6905 17.3749 16.5863 17.498 16.4758 17.6153C16.6127 17.5821 16.754 17.5715 16.8943 17.5836C17.0316 17.479 17.1879 17.4021 17.3547 17.3574C18.6915 17.6074 19.2899 17.0509 20.085 14.7153C18.4416 15.0196 17.3105 16.2954 17.2246 17.2047H17.2257ZM15.4749 18.7147C16.2994 19.3752 17.0719 19.2802 17.9123 18.1616C16.6432 17.4276 16.1105 17.8087 15.4737 18.7147H15.4749ZM14.3913 18.2295C14.7275 18.2618 15.05 18.3785 15.329 18.5688C15.5081 18.3001 15.727 18.0603 15.9782 17.8574C16.1957 17.355 16.3452 16.8259 16.4227 16.2841C15.3663 16.6019 14.6865 17.3111 14.3902 18.2295H14.3913ZM13.12 18.7712C12.8507 18.8665 12.5974 19.0022 12.369 19.1739L12.1428 19.3254C12.2288 19.3775 12.3204 19.4295 12.4177 19.4815C12.6036 19.3222 12.7996 19.1753 13.0047 19.0415C13.62 19.7281 14.7374 19.8333 15.8391 19.3322C15.0541 18.3064 14.0532 18.1876 13.1189 18.7712H13.12ZM11.557 19.658C11.1026 19.8776 10.6142 20.0183 10.1126 20.0742L9.99951 20.0878L10.6238 20.6058L10.6487 20.6001C11.1011 20.487 11.5072 20.2144 11.8816 19.9215L11.6633 19.7405L11.557 19.658Z' fill='white'/%3E%3Cpath d='M4.23554 11.0733C3.68133 10.4274 3.71187 9.32693 4.26607 8.16423C4.50808 8.48521 4.70837 8.83562 4.86213 9.20704C4.81344 9.58271 4.70179 9.94749 4.53187 10.2861C4.47305 10.4173 4.41877 10.5405 4.37126 10.6695C4.32376 10.7984 4.27399 10.9477 4.23554 11.0733ZM8.30049 18.8627C8.28239 18.8424 8.18739 18.7688 8.10934 18.7032C7.77159 18.4111 7.45907 18.091 7.17511 17.7464L7.12082 17.6774C6.79734 17.6514 6.42183 17.7328 5.96942 17.9534C6.42183 19.0222 7.51103 19.3389 8.3265 18.891L8.30049 18.8627ZM8.86601 6.7165C8.60124 6.6595 8.32966 6.64084 8.05958 6.66108C8.03017 6.71311 7.9985 6.764 7.96457 6.81264C7.84869 6.97947 7.70102 7.12179 7.53003 7.23145C7.35904 7.34111 7.16809 7.41595 6.96813 7.45167C7.71461 7.64621 8.3299 7.49126 8.86601 6.7165ZM8.90673 6.51404L9.12049 6.55702C9.75387 6.49368 10.3194 6.16681 10.7797 5.5176C10.5965 5.46218 10.4178 5.4158 10.2425 5.38074C9.74822 5.81619 9.11144 6.03222 8.29144 6.05936C8.26032 6.19278 8.21794 6.32332 8.16476 6.44957C8.41375 6.44215 8.66275 6.46379 8.90673 6.51404ZM10.8781 4.09814C9.67018 4.15243 8.82755 4.48496 8.36609 5.06405C8.38374 5.32577 8.37236 5.58864 8.33216 5.84786C9.49373 5.80375 10.3488 5.33211 10.8781 4.09814ZM6.42862 7.50031C6.38869 7.63464 6.34072 7.76646 6.28498 7.89504L6.34379 7.8679C6.57701 7.66919 6.79761 7.45614 7.00432 7.22999C8.03809 6.97324 8.3265 5.74833 8.09012 4.52794C6.93533 5.39318 6.42297 6.2539 6.80865 7.15873C6.68888 7.28146 6.56198 7.39703 6.42862 7.50483V7.50031ZM4.67777 10.3347C4.61896 10.4636 4.56467 10.5858 4.51943 10.7113C4.44026 10.9285 4.37918 11.1219 4.33281 11.2949C4.41309 11.4267 4.4856 11.563 4.54997 11.7032C4.59408 11.477 4.65628 11.2079 4.74564 10.9025C4.85118 10.6975 4.97576 10.5028 5.11775 10.3211C5.2025 10.1857 5.30064 10.0591 5.41069 9.94335C6.37772 9.94335 7.25428 9.08942 7.38548 7.75592C6.15265 7.98213 5.14376 8.66867 5.30324 9.83138C5.24103 9.91055 5.18222 9.99312 5.12453 10.0779C5.06685 10.1628 4.99333 10.2657 4.91868 10.3573C5.08495 9.84721 5.23764 9.35521 5.22633 8.90506C5.26612 8.81636 5.31341 8.73122 5.36771 8.65057C5.5245 8.42549 5.72394 8.23335 5.95472 8.08505C6.23069 7.61567 6.35284 7.04789 6.50327 6.12496C4.75016 6.78097 4.5805 8.05565 5.03292 8.98084C5.02161 9.56898 4.84177 9.97276 4.67777 10.3392V10.3347ZM6.1289 10.0022C5.258 10.6197 4.77165 11.1603 4.56354 11.7304C4.78655 12.2239 4.87998 12.7662 4.83499 13.3059C5.77375 12.5606 5.93323 12.0629 6.1289 10.0067V10.0022ZM4.26607 14.3284C4.35822 14.4078 4.45572 14.4807 4.55788 14.5467C4.85854 14.6463 5.14574 14.7827 5.41295 14.9527C5.33463 14.8175 5.27129 14.6741 5.22406 14.5252C5.01833 14.1783 4.88412 13.7937 4.82933 13.3941C4.80616 13.6048 4.76606 13.8132 4.70944 14.0174C4.7705 14.1974 4.84615 14.3722 4.93565 14.5399C4.8791 14.5048 4.82255 14.4766 4.76939 14.4494C4.64752 14.3908 4.53352 14.3171 4.43008 14.23C4.76939 13.2234 4.79879 12.0448 3.67002 10.752C2.90091 12.4678 3.51167 14.1169 4.26607 14.3329V14.3284ZM6.27254 12.029C5.03971 13.4134 5.19805 14.3974 5.80542 15.162C5.81673 15.197 5.82804 15.2321 5.84048 15.266C5.99338 15.3945 6.1363 15.5344 6.26801 15.6845C6.26801 15.6517 6.27593 15.6178 6.27932 15.5838C6.19015 15.4434 6.1126 15.2958 6.04746 15.1427C6.65822 14.627 7.131 13.3466 6.27254 12.0335V12.029ZM6.72495 17.3584C6.77246 17.3702 6.81928 17.3845 6.8652 17.4014C6.67595 17.2175 6.52549 16.9976 6.42273 16.7546C6.31996 16.5115 6.26697 16.2504 6.26688 15.9865C5.9682 15.6003 5.59568 15.2775 5.17101 15.0367C4.74634 14.7959 4.27802 14.642 3.7933 14.584C4.64271 17.0802 5.27496 17.6491 6.7193 17.363L6.72495 17.3584ZM7.19207 17.4071C7.44658 17.7737 7.74899 18.1046 8.09125 18.3911C7.97962 18.182 7.88834 17.9627 7.81867 17.7362C7.66268 17.5717 7.5176 17.3971 7.38435 17.2137C7.73384 16.2308 7.62865 15.3633 6.78264 14.5161C6.22616 15.8779 6.45237 16.8619 7.18642 17.4116L7.19207 17.4071ZM9.68036 18.235C9.42248 17.3415 8.79702 16.6267 7.79605 16.2545C7.8198 17.2996 8.09125 18.1422 8.55611 18.6897C8.87431 18.427 9.26565 18.2687 9.67696 18.2361L9.68036 18.235ZM13.9263 20.0922C12.7952 19.9564 12.1573 19.5447 11.645 19.2133C11.4467 19.0767 11.2375 18.9568 11.0195 18.8548C10.057 18.1977 9.01191 18.2893 8.20209 19.3502C9.29354 19.849 10.4031 19.7495 11.0206 19.0776C11.2378 19.2201 11.4629 19.4011 11.6993 19.5888C12.2026 19.9972 12.7737 20.4586 13.3958 20.6181L13.4218 20.6249L14.0462 20.1069L13.9263 20.0922Z' fill='white'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_2594_15344'%3E%3Crect width='24' height='24' rx='12' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
                }

                img {
                    width: 24px;
                    height: 24px;
                }

                .name {
                    @include text(16px, 400, var(--brown1), 19.2px);

                    .address {
                        @include text(16px, 400, #818E95, 19.2px);
                    }
                }
            }

            .dimmy-requisite {
                display: flex;
                align-items: center;
                flex: 1;
                justify-content: space-between;

                p {
                    @include text(16px, 400, #BFBFBF);
                }
            }

            .svg-arrow-right {
                width: 16px;
                height: 16px;
                background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 7.99967C11.9922 7.73662 11.8903 7.51114 11.6787 7.30821L5.58276 1.58855C5.40255 1.4232 5.19099 1.33301 4.93242 1.33301C4.40744 1.33301 4 1.72384 4 2.22741C4 2.46792 4.10186 2.6934 4.28208 2.86627L9.7669 7.99967L4.28208 13.1331C4.10186 13.306 4 13.5239 4 13.7719C4 14.2755 4.40744 14.6663 4.93242 14.6663C5.18315 14.6663 5.40255 14.5761 5.58276 14.4108L11.6787 8.68363C11.8981 8.48821 12 8.26273 12 7.99967Z' fill='%23D5D7D8'/%3E%3C/svg%3E%0A");
            }
        }

        & + .psystems-wrapper {
            margin-top: 24px;
        }
    }

    .psystems-wrapper {
        .title {
            margin: 0px 16px;
            font-style: normal;
            font-weight: 400;
            font-size: 12px;
            line-height: 18px;
            text-transform: uppercase;
            color: #818E95;
        }

        .title + .psystem {
            margin-top: 8px;
        }

        .psystem {
            position: relative;
            cursor: pointer;

            &:active {
                background-color: var(--f7-list-link-pressed-bg-color);
            }

            &:after {
                content: '';
                position: absolute;
                background-color: var(--f7-list-border-color);
                display: block;
                z-index: 15;
                top: auto;
                right: auto;
                bottom: 0;
                left: 0;
                height: 1px;
                width: 100%;
                transform-origin: 50% 100%;
                transform: scaleY(calc(1 / var(--f7-device-pixel-ratio)));
            }

            &:before {
                content: '';
                position: absolute;
                background-color: var(--f7-list-border-color);
                display: block;
                z-index: 15;
                top: 0;
                right: auto;
                bottom: auto;
                left: 0;
                height: 1px;
                width: 100%;
                transform-origin: 50% 0%;
                transform: scaleY(calc(1 / var(--f7-device-pixel-ratio)));
            }

            height: 48px;
            display: flex;
            background-color: #FFFFFF;
            //width: calc(100% - 32px);
            align-items: center;
            padding: 0px 16px;

            img {
                width: 24px;
                height: 24px;
            }

            &__name {
                margin-left: 8px;
                flex: 1;
                font-style: normal;
                font-weight: 400;
                font-size: 17px;
                line-height: 22px;
                letter-spacing: -0.41px;
                color: #000000;
            }
        }
    }

    .default-amounts {
        height: auto;
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 0px;
        margin-top: 8px;
        gap: 8px;

        .default-amount {
            cursor: pointer;
            border: 1px solid var(--f7-theme-color);
            border-radius: 16px;
            color: var(--f7-theme-color);
            font-weight: 400;
            font-size: 18px;
            line-height: 20px;
            letter-spacing: -0.41px;
            padding: 2px 12px;
            background-color: #FFFFFF;

            &:active {
                background-color: var(--f7-theme-color);
                color: #FFFFFF;
            }

            &.selected {
                background-color: var(--f7-theme-color);
                color: #FFFFFF;
            }
        }
    }

    .input-amount {
        &.fractional-numbers-disabled {
            background: #f3f4f5;
        }

        input {
            height: 62px !important;
            font-weight: 400 !important;
            font-size: 26px !important;
            line-height: 120% !important;
            letter-spacing: -0.41px !important;
        }

        .content {
            height: 62px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 16px;
            cursor: pointer;

            .max-amount {
                margin-left: 16px;
                color: var(--f7-theme-color);
                font-style: normal;
                font-weight: 500;
                font-size: 15px;
                line-height: 20px;
            }

            .currency-name {
                margin-left: 16px;
                color: var(--f7-theme-color);
                font-style: normal;
                font-weight: 500;
                font-size: 15px;
                line-height: 20px;
            }
        }
    }

    .actions {
        margin-top: 40px;
        margin-bottom: 16px !important;
    }

    .list {
        margin-bottom: 0;

        & + .actions {
            margin-top: 40px;
        }
    }

    .block-header {
        font-style: normal;
        font-weight: 400;
        font-size: 12px;
        line-height: 120%;
        color: #818E95;
        //margin-bottom: 8px;
    }

    .message-error {
        margin-top: 56px;
    }

    .minmax-error {
        color: var(--red);
    }
}
</style>
